import { useQuery } from '@tanstack/react-query';
import {
  apiGetAutoQuoteCarriers,
  apiGetPropertyQuoteCarriers,
  apiGetAutoQuoteMcValidations,
  apiGetPropertyQuoteMcValidations,
  type CarriersData,
} from 'api';
import { LOB_AUTO, type QuoteLOB } from '../constants';

interface UseValidationsStepDataProps {
  quoteId: number;
  quoteLob: QuoteLOB;
  enabled: boolean;
}

export interface ValidationsStepState {
  isLoading: boolean;
  error: Error | null;
  hasCarrierSelected: boolean;
  selectedCarrierQuoteId: number | null;
  validations: Validation[];
  carriersData: CarriersData | undefined;
}

export const useValidationsStepData = ({
  quoteId,
  quoteLob,
  enabled,
}: UseValidationsStepDataProps): ValidationsStepState => {
  const {
    data: carriersData,
    isLoading: isCarriersLoading,
    error: carriersError,
  } = useQuery({
    queryKey: ['marketplace-quote', quoteId, 'carriers-state'],
    queryFn: () =>
      quoteLob === LOB_AUTO
        ? apiGetAutoQuoteCarriers(quoteId)
        : apiGetPropertyQuoteCarriers(quoteId),
    enabled,
    staleTime: 30 * 1000,
  });

  const hasCarrierSelected = carriersData?.selected_carrier_quote_id != null;

  const {
    data: mcValidationsData,
    isLoading: isMcValidationsLoading,
    error: mcValidationsError,
  } = useQuery({
    queryKey: ['marketplace-quote', quoteId, 'mc-validations'],
    queryFn: () =>
      quoteLob === LOB_AUTO
        ? apiGetAutoQuoteMcValidations(quoteId)
        : apiGetPropertyQuoteMcValidations(quoteId),
    enabled: enabled && hasCarrierSelected,
    staleTime: 0,
  });

  const isLoading =
    isCarriersLoading || (hasCarrierSelected && isMcValidationsLoading);
  const error = carriersError || mcValidationsError || null;

  return {
    isLoading,
    error,
    hasCarrierSelected,
    selectedCarrierQuoteId: carriersData?.selected_carrier_quote_id ?? null,
    validations: mcValidationsData || [],
    carriersData,
  };
};
